import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { selectIsAuthenticated } from '@/store/authSlice'
import { API_BASE } from '@/config/api'

/**
 * Hook that manages push notification registration and listeners.
 * Call once in the app root (e.g. MainLayout).
 */
export const usePushNotifications = () => {
    const isAuthenticated = useSelector(selectIsAuthenticated)
    const token = useSelector((state) => state.auth.token)
    const registered = useRef(false)

    useEffect(() => {
        // Only run on native platforms (not web)
        if (!Capacitor.isNativePlatform()) return
        if (!isAuthenticated) return
        if (registered.current) return

        const register = async () => {
            // Request permission
            const permResult = await PushNotifications.requestPermissions()
            if (permResult.receive !== 'granted') {
                console.warn('Push notification permission denied')
                return
            }

            // Register with FCM
            await PushNotifications.register()

            // Listen for the FCM token
            PushNotifications.addListener('registration', async ({ value: fcmToken }) => {
                console.log('FCM Token:', fcmToken)
                // Send token to backend so it can send us notifications later
                try {
                    await fetch(`${API_BASE}/push/register`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-access-token': token
                        },
                        body: JSON.stringify({ token: fcmToken })
                    })
                } catch (err) {
                    console.error('Failed to register FCM token with backend:', err)
                }
            })

            // Listen for registration errors
            PushNotifications.addListener('registrationError', (error) => {
                console.error('Push registration error:', error)
            })

            // Listen for incoming notifications while app is open (foreground)
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log('Push received (foreground):', notification)
                // You could show a snackbar/toast here
            })

            // Listen for notification tap (user tapped the notification)
            PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
                console.log('Push action performed:', action)
                // You could navigate to a specific page based on action.notification.data
            })

            registered.current = true
        }

        register()

        return () => {
            PushNotifications.removeAllListeners()
            registered.current = false
        }
    }, [isAuthenticated, token])
}
